import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Phone } from 'lucide-react';
import { services, companyInfo } from '../data/mock';
import ContactFormModal from './ContactFormModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, [location]);

  const handleServicesEnter = () => {
    setIsServicesOpen(true);
  };

  const handleServicesLeave = () => {
    setIsServicesOpen(false);
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/assets/img/brand/logo-dark.png" 
                alt="IP Partners Inc" 
                className="h-9 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              <Link
                to="/about"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                About Us
              </Link>

              {/* Services Dropdown */}
              <div
                className="relative"
                onMouseEnter={handleServicesEnter}
                onMouseLeave={handleServicesLeave}
              >
                <button className="flex items-center space-x-1 text-gray-700 hover:text-brown-700 font-medium transition-colors duration-200">
                  <span>Services</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {/* Mega Menu Dropdown */}
                {isServicesOpen && (
                  <div className="absolute left-1/2 transform -translate-x-1/2 top-full pt-2 w-80">
                    <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-4 max-h-96 overflow-y-auto">
                      {services.map((service) => (
                        <Link
                          key={service.slug}
                          to={`/services/${service.slug}`}
                          className="block px-6 py-3 text-gray-700 hover:bg-brown-100 hover:text-brown-800 transition-colors duration-150"
                        >
                          {service.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/attorney"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Meet the Attorney
              </Link>

              <Link
                to="/contact"
                className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200"
              >
                Contact Us
              </Link>
            </nav>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center space-x-4">
              <a href={`tel:${companyInfo.phone}`} className="flex items-center space-x-2 text-gray-700 hover:text-brown-700 transition-colors duration-200">
                <Phone className="w-4 h-4" />
                <span className="font-medium">{companyInfo.phone}</span>
              </a>
              <button
                className="js-chat bg-white text-brown-700 px-6 py-2.5 rounded-lg font-medium border-2 border-brown-700 hover:bg-brown-50 transition-colors duration-200 shadow-sm"
              >
                Chat Now
              </button>
              <button
                onClick={() => setIsModalOpen(true)}
                className="bg-brown-700 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brown-800 transition-colors duration-200 shadow-sm"
              >
                Get Legal Help
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-gray-700 hover:text-brown-700"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
              <Link to="/about" className="block py-2 font-medium text-gray-700">
                About Us
              </Link>
              
              <div className="border-b border-gray-100 pb-4">
                <div className="font-semibold text-gray-900 mb-2">Services</div>
                <div className="space-y-2 pl-4">
                  {services.map((service) => (
                    <Link
                      key={service.slug}
                      to={`/services/${service.slug}`}
                      className="block py-2 text-gray-600 hover:text-brown-700"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/attorney" className="block py-2 font-medium text-gray-700">
                Meet the Attorney
              </Link>

              <Link to="/contact" className="block py-2 font-medium text-gray-700">
                Contact Us
              </Link>
              
              <div className="space-y-3 pt-2">
                <button
                  className="js-chat w-full bg-white text-brown-700 px-6 py-3 rounded-lg font-medium border-2 border-brown-700 hover:bg-brown-50 transition-colors duration-200"
                >
                  Chat Now
                </button>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="w-full bg-brown-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-brown-800 transition-colors duration-200"
                >
                  Get Legal Help
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      <ContactFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default Header;
